
import React, { useState, useEffect, Suspense, lazy } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { AppRole } from './types/index';
import { firebaseService } from './services/firebase';
import ErrorBoundary from './components/ErrorBoundary';

const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const AdminLogin = lazy(() => import('./components/AdminLogin'));
const ClientAccess = lazy(() => import('./components/ClientAccess'));
const ClientGallery = lazy(() => import('./components/ClientGallery'));
const ManualSorter = lazy(() => import('./components/ManualSorter'));

const App: React.FC = () => {
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(false);
  const [activeClientSession, setActiveClientSession] = useState<any>(null);
  const [view, setView] = useState<'default' | 'manual-sorter'>('default');
  const [isAdminPath, setIsAdminPath] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [isRestoringSession, setIsRestoringSession] = useState(true);

  // Check auth state on mount
  useEffect(() => {
    const unsubscribe = firebaseService.getAuth().onAuthStateChanged((user) => {
      setIsAdminAuthenticated(!!user);
      setAuthChecked(true);
    });
    
    return unsubscribe;
  }, []);

  // Restore client session from localStorage
  useEffect(() => {
    const restoreSession = async () => {
      const savedSessionId = localStorage.getItem('clientSessionId');
      if (savedSessionId) {
        try {
          const session = await firebaseService.getSessionById(savedSessionId);
          if (session) {
            setActiveClientSession(session);
          } else {
            localStorage.removeItem('clientSessionId');
          }
        } catch (error) {
          console.error('Failed to restore session:', error);
          localStorage.removeItem('clientSessionId');
        }
      }
      setIsRestoringSession(false);
    };
    
    restoreSession();
  }, []);

  // Simple hash-based router for security and separation
  useEffect(() => {
    const handleHashChange = () => {
      setIsAdminPath(window.location.hash === '#admin');
    };
    
    // Initial check
    handleHashChange();
    
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  // Show loading while checking auth
  if (!authChecked || isRestoringSession) {
    return (
      <div className="min-h-screen bg-[#FDFDFD] flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="w-8 h-8 border-2 border-stone-300 border-t-stone-600 rounded-full"
        />
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <Suspense
        fallback={
          <div className="min-h-screen bg-[#FDFDFD] flex items-center justify-center">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              className="w-8 h-8 border-2 border-stone-300 border-t-stone-600 rounded-full"
            />
          </div>
        }
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          {/* Admin Flow */}
          {isAdminPath ? (
            <>
              {!isAdminAuthenticated ? (
                <motion.div
                  key="admin-login"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <AdminLogin 
                    onLoginSuccess={() => {
                      setIsAdminAuthenticated(true);
                      toast.success('Welcome to Admin Dashboard');
                    }} 
                    onBack={() => {
                      window.location.hash = '';
                      toast.success('Redirected to Client Portal');
                    }} 
                  />
                </motion.div>
              ) : view === 'manual-sorter' ? (
                <motion.div
                  key="manual-sorter"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <ManualSorter onBack={() => setView('default')} />
                </motion.div>
              ) : (
                <motion.div
                  key="admin-dashboard"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  <AdminDashboard 
                    onLogout={() => {
                      setIsAdminAuthenticated(false);
                      toast.success('Logged out successfully');
                    }} 
                    onOpenManualSorter={() => {
                      setView('manual-sorter');
                      toast.success('Opening Manual Sorter...');
                    }} 
                  />
                </motion.div>
              )}
            </>
          ) : (
            <>
              {/* Client Flow (Default) */}
              {!activeClientSession ? (
                <motion.div
                  key="client-access"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <ClientAccess 
                    onAccessSuccess={(session) => {
                      localStorage.setItem('clientSessionId', session.id);
                      setActiveClientSession(session);
                      toast.success('Access granted! Loading your gallery...');
                    }} 
                    onBack={() => {}} // No back needed for clients
                  />
                </motion.div>
              ) : (
                <motion.div
                  key="client-gallery"
                  initial={{ opacity: 0, scale: 0.98 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  <ClientGallery 
                    session={activeClientSession} 
                    onExit={() => {
                      localStorage.removeItem('clientSessionId');
                      setActiveClientSession(null);
                      toast.success('Session ended. Thank you!');
                    }} 
                  />
                </motion.div>
              )}
            </>
          )}
        </motion.div>
      </Suspense>
    </ErrorBoundary>
  );
};

export default App;
